import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Format } from 'applicaiton/constants/scheduler';
import { store } from 'applicaiton/store';

dayjs.extend(utc, localizedFormat);
dayjs.extend(timezone);

/**
 * Rounds the minutes of a given date to the nearest 30-minute mark.
 * @param date - The date to be rounded.
 * @returns A new Date object with the minutes rounded to the nearest 30-minute mark.
 */
export const getTimeWithRoundedMinutes = (date: Date) => {
  const dateObj = dayjs(date);
  const currentDateMinute = dateObj.get('minute');
  return dateObj
    .set('minute', Math.ceil(currentDateMinute / 30) * 30)
    .set('second', 0)
    .toDate();
};

/**
 * Adds a specified number of minutes to a given date.
 * @param date - The date to add minutes to.
 * @param minutes - The number of minutes to add.
 * @returns A new Date object with the minutes added.
 */
export const addMinutes = (date: Date, minutes: number) => {
  return dayjs(date).add(minutes, 'minutes').toDate();
};

/**
 * Formats a given date in the default timezone according to a specified format.
 * @param date - The date to be formatted.
 * @param format - The format to use for formatting the date.
 * @param locale - (Optional) The locale to use for formatting the date.
 * @returns A string representing the formatted date.
 */
export const formatDateTimeInDefaultTimezone = (
  date: Date | string,
  format: string,
  locale?: string | ILocale,
) => {
  const DEFAULT_TIMEZONE = store.getState().timeZone.tz;
  let dateInTz = dayjs.tz(date, DEFAULT_TIMEZONE);
  dateInTz = dateInTz.isUTC() ? dateInTz : dayjs(date).tz(DEFAULT_TIMEZONE);
  if (locale) {
    dateInTz = dateInTz.locale(locale);
  }
  return dateInTz.format(format);
};

/**
 * Converts a given date from UTC to the default timezone.
 * @param date - The date to be converted.
 * @returns A new Date object representing the date in the default timezone.
 */
export const fromUtcToDefaultTimezone = (date: Date) => {
  const DEFAULT_TIMEZONE = store.getState().timeZone.tz;
  const dateInTz = dayjs(date).tz(DEFAULT_TIMEZONE);
  return new Date(dateInTz.format(Format.dateTimeFormat));
};

/**
 * Gets a Day.js object representing the given date in UTC converted to the default timezone.
 * @param date - The date to be converted.
 * @returns A Day.js object representing the date in UTC converted to the default timezone.
 */
export const getDayjsFromUTCInDefaultTimezone = (date: Date | null) => {
  const DEFAULT_TIMEZONE = store.getState().timeZone.tz;
  return dayjs(date).tz(DEFAULT_TIMEZONE);
};

/**
 * Gets a Day.js object representing the given date in the default timezone, accounting for daylight saving time.
 * @param date - The date to be converted.
 * @returns A Day.js object representing the date in the default timezone, accounting for daylight saving time.
 */
export const getDateInDefaultTimezone = (date: Date | null) => {
  const DEFAULT_TIMEZONE = store.getState().timeZone.tz;
  let dateInTz = dayjs.tz(date, DEFAULT_TIMEZONE);
  return dateInTz.isUTC() ? dateInTz : dayjs(date).tz(DEFAULT_TIMEZONE);
};

/**
 * Rounds a given date to the nearest midnight in UTC.
 * @param date - The date to be rounded.
 * @returns A Day.js object representing the date rounded to the nearest midnight in UTC.
 */
export const getRoundedDateInUTC = (date: Date | string) => {
  let resultDate = dayjs(date);
  const hours = resultDate.get('hour');
  const daysToAdd = hours >= 12 ? 1 : 0;
  resultDate = resultDate.add(daysToAdd, 'day');
  resultDate = resultDate.startOf('date');
  return resultDate;
};

/**
 * Updates the date and/or time of a given initial date time.
 * @param initialDateTime - The initial date time to be updated.
 * @param newDate - (Optional) The new date to replace the existing one.
 * @param newTime - (Optional) The new time to replace the existing one.
 * @returns A string representing the updated date and/or time.
 */
export const updateDateAndTime = (
  initialDateTime: Date,
  newDate?: string,
  newTime?: string,
) => {
  let [initDate, initTime] = initialDateTime.toString().split('T');
  if (newDate) {
    initDate = newDate;
  }
  if (newTime) {
    initTime = newTime;
  }
  return `${initDate}T${initTime}`;
};
